/**
 * This file is both a client-side and server-side utility. It should not
 * import server-side or client-side specific code, if the function requires
 * an import from a specific environment, you should use a move it to the
 * appropriate utility file.
 */

import { format } from "date-fns";
import { Nullable } from "./nullable";
import { Currencies } from "./types";
import { overwrite, getName } from "country-list";
import {
  BrandDirectoryStatus,
  CreatorOnboardingStep,
  DealMemoStatus,
  Gender,
  LinkInBioSocialService,
  PitchStatus,
  ScrapePlatform,
  SocialScrapeStep,
  SocialService,
  StreamingService,
  VideoAspectRatio,
  VideoFormat,
} from "./enums";
import { formatCompactNumber, getCurrencySymbol } from "./shared";
import { DollarSign, Euro, LucideIcon, PoundSterling } from "lucide-react";

// Structural type for Firebase Timestamp — avoids importing firebase/firestore
type TimestampLike = { toDate(): Date };

// Minimal structural type for creator name fields
type CreatorNameFields = {
  firstName: string | null;
  lastName: string | null;
  knownAs: string | null;
};

const LOCALE = "en-GB";

/**
 * Calculates Public Play Rate (Play Count / Follower Count).
 * Sourced from public post engagement data.
 * Returns null if follower count is <= 0 or play count is null/undefined/negative.
 *
 * @example
 * calculatePlayRate(2800, 10000) // 0.28
 */
export const calculatePlayRate = (
  playCount: number | null | undefined,
  followersCount: number | null | undefined,
): number | null => {
  if (
    playCount === null ||
    playCount === undefined ||
    playCount < 0 ||
    followersCount === null ||
    followersCount === undefined ||
    followersCount <= 0
  ) {
    return null;
  }
  return Number((playCount / followersCount).toFixed(4));
};

/**
 * Formats a Play Rate fraction (e.g. 0.28 -> "28.0% (0.28x)" or "28.0%" or "0.28x").
 */
export const formatPlayRate = (
  playRate: number | null | undefined,
  format: "percentage" | "multiplier" | "full" = "full",
): string => {
  if (playRate === null || playRate === undefined) {
    return "—";
  }
  if (format === "percentage") {
    return `${(playRate * 100).toFixed(1)}%`;
  }
  if (format === "multiplier") {
    return `${playRate.toFixed(2)}x`;
  }
  return `${(playRate * 100).toFixed(1)}% (${playRate.toFixed(2)}x)`;
};

/**
 * Calculates Private VTR (100% − skip rate) as a percentage.
 * Skip rate is Meta's `reels_skip_rate` (percentage of plays skipped within the
 * first 3 seconds), only available for Graph API connected creators.
 * Returns null if skip rate is missing or outside 0–100.
 *
 * @example
 * calculatePrivateVtr(41.3) // 58.7
 */
export const calculatePrivateVtr = (
  skipRate: number | null | undefined,
): number | null => {
  if (
    skipRate === null ||
    skipRate === undefined ||
    Number.isNaN(skipRate) ||
    skipRate < 0 ||
    skipRate > 100
  ) {
    return null;
  }
  return Number((100 - skipRate).toFixed(2));
};

/**
 * Formats a Skip Rate (3s) percentage from Private VTR (100% − privateVtr).
 * Meta's `reels_skip_rate` represents plays skipped within the first 3 seconds.
 * Returns null if privateVtr is missing or outside 0–100.
 *
 * @example
 * formatSkipRate(66) // "34%"
 * formatSkipRate(65.8) // "34.2%"
 * formatSkipRate(null) // null
 */
export const formatSkipRate = (
  privateVtr: number | null | undefined,
): string | null => {
  if (
    privateVtr === null ||
    privateVtr === undefined ||
    Number.isNaN(privateVtr) ||
    privateVtr < 0 ||
    privateVtr > 100
  ) {
    return null;
  }
  return `${Number((100 - privateVtr).toFixed(1))}%`;
};

/**
 * Formats a Replay Rate multiplier (views / reach) (e.g. 1.1x).
 * Sourced from post views and reach.
 * Returns null if reach is <= 0 or views is null/undefined/negative.
 *
 * @example
 * formatReplayRate(23403, 21304) // "1.1x"
 * formatReplayRate(null, 100) // null
 */
export const formatReplayRate = (
  views: number | null | undefined,
  reach: number | null | undefined,
): string | null => {
  if (
    views === null ||
    views === undefined ||
    Number.isNaN(views) ||
    views < 0 ||
    reach === null ||
    reach === undefined ||
    Number.isNaN(reach) ||
    reach <= 0
  ) {
    return null;
  }
  return `${(views / reach).toFixed(1)}x`;
};

/**
 * Calculates Private Average Watch Time % (avg watch time / duration) as a
 * percentage. Average watch time is Meta's `ig_reels_avg_watch_time`, only
 * available for Graph API connected creators. Both inputs are in seconds.
 * Returns null if either input is missing, negative, or duration is 0.
 *
 * @example
 * calculateAvgWatchTimePct(7.8, 15) // 52
 */
export const calculateAvgWatchTimePct = (
  avgWatchTimeSeconds: number | null | undefined,
  durationSeconds: number | null | undefined,
): number | null => {
  if (
    avgWatchTimeSeconds === null ||
    avgWatchTimeSeconds === undefined ||
    Number.isNaN(avgWatchTimeSeconds) ||
    avgWatchTimeSeconds < 0 ||
    durationSeconds === null ||
    durationSeconds === undefined ||
    Number.isNaN(durationSeconds) ||
    durationSeconds <= 0
  ) {
    return null;
  }
  return Number(((avgWatchTimeSeconds / durationSeconds) * 100).toFixed(2));
};

export const timeAgo = (date: Date, now: Date = new Date()): string => {
  const diffInSeconds = (now.getTime() - date.getTime()) / 1000;

  switch (true) {
    case diffInSeconds < 60: // Less than a minute ago
      return "now";
    case diffInSeconds < 3600: {
      // Less than an hour ago
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    }
    case diffInSeconds < 86400: {
      // Less than a day ago
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    }
    default: // More than a day ago
      return format(date, "dd MMM ''yy");
  }
};

export const getRelativeTime = (date: Date): string => {
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
};

export const formatCreatorName = (creator: Nullable<CreatorNameFields>) => {
  if (creator?.knownAs != null && creator.knownAs.length > 0) {
    return creator.knownAs;
  } else if (
    (creator?.firstName != null && creator.firstName.length > 0) ||
    (creator?.lastName != null && creator.lastName.length > 0)
  ) {
    return `${creator.firstName || ""} ${creator.lastName ?? ""}`.trim();
  } else {
    return "the creator";
  }
};

// Minimal structural type for creator page title fallback fields
type CreatorPageTitleFields = {
  tiktokHandle: string | null;
  instagramHandle: string | null;
  knownAs: string | null;
  firstName: string | null;
};

const normalizeHandle = (handle: string | null | undefined) =>
  handle != null && handle.trim().length > 0
    ? `@${handle.trim().replace(/^@+/, "")}`
    : null;

/**
 * Builds a unique, SEO-friendly identifier for creator page titles by
 * preferring social handles over names, since names are often shared
 * between creators and cause duplicate page titles.
 * Falls back through: TikTok handle > Instagram handle > known as > first name > `fallback`.
 */
export const formatCreatorPageTitleName = (
  creator: Nullable<CreatorPageTitleFields>,
  fallback: string,
): string =>
  normalizeHandle(creator?.tiktokHandle) ??
  normalizeHandle(creator?.instagramHandle) ??
  (creator?.knownAs != null && creator.knownAs.trim().length > 0
    ? creator.knownAs.trim()
    : null) ??
  (creator?.firstName != null && creator.firstName.trim().length > 0
    ? creator.firstName.trim()
    : null) ??
  fallback;

/**
 * This function is to get the PandaDoc contract page URL only for DRPCRD Admins
 * This page allows edits to the contract, and to see all detailed stats.
 * This is not for normal users!
 */
export const getPandaDocContractAdminPageUrl = (
  contractDocumentId: string,
): string => {
  const baseURL = "https://app.pandadoc.com/a/#/documents/";
  return baseURL + contractDocumentId;
};

export const mailToLink = ({
  subject,
  body = "",
  to,
}: {
  subject: string;
  body?: string;
  to: string;
}): string =>
  `mailto:?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(body)}&to=${encodeURIComponent(to)}`;

// 11:45am, 3 Sep 2024
export const formatDate = (date: Date): string => {
  const d = new Date(date);
  const time = d
    .toLocaleTimeString(LOCALE, {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase()
    .replace(" ", "");
  const dateStr = d.toLocaleDateString(LOCALE, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return `${time}, ${dateStr}`;
};

// 16 March 2023 at 6:00 pm
export const formatDatetime = (date: Date): string =>
  new Intl.DateTimeFormat(LOCALE, {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
    timeZone: "UTC",
  }).format(new Date(date));

// 16 Mar 23
export const formatDateWithoutTime = (date: Date): string =>
  new Date(date).toLocaleDateString(LOCALE, {
    year: "2-digit",
    month: "short",
    day: "2-digit",
  });

// 16 Mar 2023
export const formatDateWithoutTime2 = (date: Date): string =>
  new Date(date).toLocaleDateString(LOCALE, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

// 16 March 2023
export const formatDateWithoutTimeExtended = (date: Date): string =>
  new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
  ).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

// May 2026
export const formatMonthYear = (date: Date): string =>
  format(date, "MMMM yyyy");

/**
 * Formats time elapsed from a date to now.
 * Returns "Just now" for times less than a minute ago,
 * "Xm ago" under an hour, "Xh ago" under a day,
 * "X days ago" for 1-7 days ago,
 * or a full date format "14 January 2026" for anything older than 7 days.
 */
export const formatTimeElapsed = (
  date: Date,
  now: Date = new Date(),
): string => {
  const minutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

  if (minutes < 1) {
    return "Just now";
  }

  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.floor(minutes / 60);

  if (hours < 24) {
    return `${hours}h ago`;
  }

  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
  const startOfDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  const days = Math.floor(
    (startOfToday.getTime() - startOfDate.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (days <= 7) {
    return `${days} day${days === 1 ? "" : "s"} ago`;
  }

  return formatDateWithoutTimeExtended(date);
};

// 4.00PM, 16 Mar 2023
export const formatDate2 = (date: Date): string => {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const period = hours >= 12 ? "PM" : "AM";

  const formattedHours = hours % 12 === 0 ? 12 : hours % 12;
  const formattedMinutes = minutes.toString().padStart(2, "0");

  const formattedDate = `${formattedHours}.${formattedMinutes}${period}, ${date.toLocaleDateString(
    LOCALE,
    {
      day: "numeric",
      month: "short",
      year: "numeric",
    },
  )}`;

  return formattedDate;
};

/**
 * Formats a Firebase Firestore Timestamp into a human-readable date string.
 * If the date is today, it returns the time in the format: 'h.mmAM/PM, today'.
 * If the date is from the past, it returns the date in the format: '1 Jun '25'.
 *
 * @param {TimestampLike} createdAt - An object with a toDate() method (e.g. Firebase Timestamp).
 * @returns {string} The formatted date string.
 */
export const formatFirebaseDate = (
  createdAt: TimestampLike,
  showTodayAsDate = false,
): string => {
  const now = new Date();
  const createdDate = createdAt.toDate();

  if (createdDate.toDateString() === now.toDateString() && !showTodayAsDate) {
    const hours = createdDate.getHours();
    const minutes = createdDate.getMinutes();
    const suffix = hours >= 12 ? "pm" : "am";
    return `${hours % 12 || 12}.${minutes < 10 ? "0" + minutes : minutes}${suffix}, today`;
  }

  const day = createdDate.getDate();
  const month = createdDate.toLocaleString("default", { month: "short" });
  const year = createdDate.getFullYear().toString().slice(-2);
  return `${day} ${month} ‘${year}`;
};

/**
 * Formats a date for the Inbox notification list:
 * - Today    → "2.34pm"
 * - Same year → "3 Jan"
 * - Other year → "31 Dec '26"
 */
export const formatInboxTimestamp = (
  date: Date,
  now: Date = new Date(),
): string => {
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const suffix = hours >= 12 ? "pm" : "am";
    return `${hours % 12 || 12}.${minutes.toString().padStart(2, "0")}${suffix}`;
  }

  const day = date.getDate();
  const month = date.toLocaleString("default", { month: "short" });

  if (date.getFullYear() === now.getFullYear()) {
    return `${day} ${month}`;
  }

  const year = date.getFullYear().toString().slice(-2);
  return `${day} ${month} '${year}`;
};

/**
 * Formats date as: 1:33pm, 21 Nov '25
 */
export const formatDatetimeWithDayAndShortYear = (date: Date): string => {
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const period = hours >= 12 ? "pm" : "am";
  const formattedHours = hours % 12 || 12;

  const day = date.getDate();
  const month = date.toLocaleDateString("en-GB", { month: "short" });
  const year = date.getFullYear().toString().slice(-2);

  return `${formattedHours}:${minutes}${period}, ${day} ${month} ‘${year}`;
};

/**
 * Formats date as: 21 Nov ’25
 */
export const formatDateWithShortYear = (date: Date): string => {
  const day = date.getDate();
  const month = date.toLocaleDateString("en-GB", { month: "short" });
  const year = date.getFullYear().toString().slice(-2);

  return `${day} ${month} ’${year}`;
};

export const capitalizeFirstLetter = (str: string): string =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

export const toCurrency = (
  amount: string | number,
  currency = "GBP",
  minimumFractionDigits = 2,
) => {
  const normalizedCurrency = currency ? currency.toUpperCase() : "GBP";
  try {
    return new Intl.NumberFormat(LOCALE, {
      style: "currency",
      currency: normalizedCurrency,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits,
    }).format(Number(amount) / 100);
  } catch {
    return new Intl.NumberFormat(LOCALE, {
      style: "currency",
      currency: "GBP",
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits,
    }).format(Number(amount) / 100);
  }
};

/**
 * Takes a float value and formats it as a percentage with 2 decimal places.
 * @example formatPercentage(0.1234) => "12.34%"
 */
export const formatPercentage = (num: number, decimalPlaces?: number) =>
  new Intl.NumberFormat(LOCALE, {
    style: "percent",
    maximumFractionDigits: decimalPlaces ?? 2,
  }).format(num);

/**
 * This function takes any form of number and formats it as a string with commas.
 * @example formatNum(1234567) => "1,234,567"
 * @example formatNum(1234567.89) => "1,234,567.89"
 */
export const formatNum = (num: number) =>
  new Intl.NumberFormat(LOCALE).format(num);

/**
 * Formats milliseconds into a timer display format (M:SS).
 * Commonly used for video recording or playback duration displays.
 * @param ms - The duration in milliseconds to format.
 */
export const formatMillisecondsToTimer = (ms: number): string => {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

/**
 * This function formats a number in seconds to a short human-readable string.
 * @example formatSeconds(90061) => "1d 1h 1m 1s"
 * @example formatSeconds(3661) => "1h 1m 1s"
 * @example formatSeconds(61) => "1m 1s"
 */
export const formatSeconds = (seconds: number) => {
  const days = Math.floor(seconds / (3600 * 24));
  const hours = Math.floor((seconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const parts = [
    days && `${days}d`,
    hours && `${hours}h`,
    minutes && `${minutes}m`,
    remainingSeconds && `${remainingSeconds}s`,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(" ") : "0s";
};

export const toSentenceCase = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1);

// Defaults to ISO 3166-1 alpha-2: United Kingdom of Great Britain and Northern Ireland
overwrite([{ code: "GB", name: "United Kingdom" }]);

/**
 * Gets the country name from ISO code and removes unnecessary official suffixes.
 */
export const getCountryName = (countryCode: string) => {
  const name = getName(countryCode);
  return name?.replace(/\s*\([^)]*\)\s*$/g, "").trim() || countryCode;
};

/**
 * Converts an ISO 3166-1 alpha-2 country code to its flag emoji.
 */
export const getCountryFlag = (countryCode: string): string => {
  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

export const formatDeviceName = (name: string) => {
  switch (name) {
    case "ios":
      return "iOS";
    case "android":
      return "Android";
    default:
      return name;
  }
};

/**
 * Formats a CDN image URL by appending it to the NEXT_PUBLIC_IMAGE_CDN environment variable.
 * It also ensures we are being type-safe by returning the same type as the input.
 * @param src - The source URL of the image.
 * @returns The formatted CDN image URL.
 */
export const formatCdnImg = <T = Nullable<string>>(src: T): T =>
  src != null
    ? (`${process.env.NEXT_PUBLIC_IMAGE_CDN}${src}` as T)
    : (undefined as T);

/**
 * Ensures email images render correctly in Gmail by routing them
 * through a trusted proxy on our domain.
 *
 * - Converts relative or CDN paths into full absolute URLs.
 * - Wraps non-local URLs with `/api/proxy?url=...` for safe inline loading.
 */
export const formatEmailImg = <T = Nullable<string>>(src: T): T => {
  if (src == null) return undefined as T;

  const base = process.env.NEXT_PUBLIC_URL ?? "https://brand.creatoros.xyz";
  const cdn =
    process.env.NEXT_PUBLIC_IMAGE_CDN ??
    "https://d2jmt1ah33cxcs.cloudfront.net";

  const url = String(src);

  if (url.startsWith(base)) return url as T;

  const absoluteUrl = url.startsWith("http")
    ? url
    : `${cdn.replace(/\/$/, "")}/${url.replace(/^\//, "")}`;

  return `${base}/api/proxy?url=${encodeURIComponent(absoluteUrl)}` as T;
};

/**
 * Formats the follower count.
 *
 * @param count - The follower count to format.
 * @returns The formatted follower count as a string "X followers" or "Followers Coming Soon" if count is null.
 */
export const formatFollowerCount = (count: number | null): string =>
  count ? `${formatCompactNumber(count)} followers` : "Followers Coming Soon";

/**
 * Formats the average views count.
 *
 * @param count - The average views count.
 * @returns The formatted average views count as a string.
 */
export const formatAvgViews = (count: number | null): string =>
  count ? `${formatCompactNumber(count)}` : "Coming Soon";

const serviceNames: Record<SocialService, string> = {
  tiktok: "TikTok",
  instagram: "Instagram",
  facebook: "Facebook",
  youtube: "YouTube",
  twitter: "Twitter",
  snapchat: "Snapchat",
  cameo: "Cameo",
  twitch: "Twitch",
};

export const formatServiceName = (service: SocialService) =>
  serviceNames[service] ?? service;

const socialUrls: Record<SocialService, (username: string) => string> = {
  instagram: (username) => `https://instagram.com/${username}`,
  tiktok: (username) => `https://tiktok.com/@${username}`,
  youtube: (username) => `https://youtube.com/@${username}`,
  twitter: (username) => `https://twitter.com/${username}`,
  facebook: (username) => `https://facebook.com/${username}`,
  snapchat: (username) => `https://snapchat.com/add/${username}`,
  cameo: (username) => `https://cameo.com/${username}`,
  twitch: (username) => `https://twitch.tv/${username}`,
};

export const formatSocialUrl = (service: SocialService, username: string) =>
  socialUrls[service]?.(username) ?? null;

const genderNames: Record<Gender, string> = {
  male: "Male",
  female: "Female",
  "non-binary": "Non-binary",
  "prefer-not-to-say": "Prefer not to say",
  other: "Other",
  unknown: "Unknown",
};

export const formatGenderName = (gender: Gender) =>
  genderNames[gender] ?? gender;

const roundToNearest50 = (value: number) => Math.round(value / 50) * 50;

export const formatCreatorRate = (
  rate: number | null,
  currency: Currencies = "GBP",
) => {
  if (rate == null || rate <= 0) {
    return `${getCurrencySymbol(currency)}POA`;
  }
  const rateInPounds = rate / 100;
  const roundedRate = roundToNearest50(rateInPounds);
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(roundedRate);
};

export const spokeFallbacks = {
  brief: {},
  deliverables: {},
  exclusivity: {
    exclusivity: "No exclusivity details for this mission",
  },
  expenses: {
    expensesDetails: "No expenses for this mission",
    expensesTotalAllowance: 0,
  },
  filming: {
    filming: "No filming details for this mission",
  },
  perks: {
    perks: "No perks for this mission",
  },
  specialTerms: {
    specialTerms: "No special terms for this mission",
  },
};

export type SpokeType =
  | "perks"
  | "filming"
  | "deliverables"
  | "brief"
  | "exclusivity"
  | "expenses"
  | "specialTerms";

/**
 * Formats the spoke fallbacks based on the provided format.
 *
 * @param format - The format of the spoke, which can be null or a specific SpokeType.
 * @returns A partial object that can be either SelectMission or SelectDealMemo,
 *          depending on the provided format. If the format is null or not found
 *          in spokeFallbacks, an empty object is returned.
 */
export const formatSpokeFallbacks = (format: Nullable<SpokeType>) =>
  format ? (spokeFallbacks[format] ?? {}) : {};

const spokeTitles: Record<SpokeType, string> = {
  brief: "The brief",
  deliverables: "Deliverables and usage",
  exclusivity: "Exclusivity",
  expenses: "Expenses",
  filming: "Filming details",
  perks: "Perks",
  specialTerms: "Special terms",
};

/**
 * Formats the name of a spoke type.
 *
 * @param format - The video format to be formatted.
 * @returns The formatted name of the video format.
 */
export const formatSpokeTitle = (format: Nullable<SpokeType>) =>
  format ? (spokeTitles[format] ?? format) : format;

const videoAspectRatioLabels: Record<VideoAspectRatio, string> = {
  "16:9": "16:9 landscape",
  "9:16": "9:16 portrait",
  "1:1": "1:1 square",
};

/**
 * Formats the video aspect ratio.
 *
 * @param aspectRatio - The aspect ratio of the video.
 * @returns The formatted video aspect ratio.
 */
export const formatVideoAspectRatio = (
  aspectRatio: Nullable<VideoAspectRatio>,
) => videoAspectRatioLabels[aspectRatio ?? "9:16"];

/**
 * Generates a formatted upload link for a given phone number.
 *
 * This function takes a phone number as input, encodes it in base64 format,
 * and returns a URL string that includes the encoded phone number as a path parameter.
 *
 * @param phone - The phone number to be encoded and included in the upload link.
 * @returns A string representing the formatted upload link.
 */
export const formatUploadLink = (phone: string): string => {
  const encodedPhone = Buffer.from(phone).toString("base64");
  return `/creator/upload/${encodedPhone}`;
};

const platformNames: Record<VideoFormat, string> = {
  tiktok: "TikTok",
  snapchat: "Snap",
  instagram_reel: "Instagram Reel",
  instagram_stories: "Instagram Stories",
  livestream: "Live stream",
  youtube: "YouTube",
  youtube_shorts: "YouTube Shorts",
  other_deliverable: "Other Deliverable",
};

export const videoFormatLabels: Record<VideoFormat, string> = {
  tiktok: "TikTok",
  snapchat: "Snap",
  instagram_reel: "Instagram Reel",
  instagram_stories: "Instagram Stories",
  livestream: "Live stream",
  youtube: "YouTube",
  youtube_shorts: "YouTube Shorts",
  other_deliverable: "Other Deliverable",
};

const platformHandlerPrefixes: Record<VideoFormat, string> = {
  tiktok: "@",
  snapchat: "",
  instagram_reel: "@",
  instagram_stories: "@",
  livestream: "",
  youtube: "",
  youtube_shorts: "",
  other_deliverable: "",
};

/**
 * Formats the name of a video format.
 *
 * @param format - The video format to be formatted.
 * @returns The formatted name of the video format.
 */
export const formatVideoFormatName = (format: Nullable<VideoFormat>) =>
  format ? (videoFormatLabels[format] ?? "Unknown format") : "Unknown format";

/**
 * Finds the platform name based on a video format.
 *
 * @example "instagram_reel" returns "Instagram".
 *
 * @param format - The video format to be converted to a platform name.
 * @returns The corresponding platform name for the video format.
 */
export const formatVideoFormatToPlatformName = (
  format: Nullable<VideoFormat>,
): string =>
  format ? (platformNames[format] ?? "Unknown platform") : "Unknown platform";

export const formatPlatformHandlerPrefix = (format: Nullable<VideoFormat>) =>
  format ? (platformHandlerPrefixes[format] ?? "") : "";

const streamingServiceNames: Record<StreamingService, string> = {
  twitch: "Twitch",
  tiktok: "TikTok",
  instagram: "Instagram",
  youtube: "YouTube",
  twitter: "X",
  other: "Other",
};

const linkInBioSocialServiceNames: Record<LinkInBioSocialService, string> = {
  ...streamingServiceNames,
  cameo: "Cameo",
  eventbrite: "Eventbrite",
  other: "Link",
};

/**
 * Formats the label for a Link in Bio social service.
 *
 * @param service - The Link in Bio social service to be formatted.
 * @returns The formatted label for the Link in Bio social service.
 */
export const formatLinkInBioServiceLabel = (service: LinkInBioSocialService) =>
  linkInBioSocialServiceNames[service] ?? "Channel";

/**
 * Formats the name of a streaming service.
 *
 * @param service - The streaming service to be formatted.
 * @returns The formatted name of the streaming service.
 */
export const formatStreamingServiceName = (
  service: Nullable<StreamingService>,
) =>
  service
    ? (streamingServiceNames[service] ?? "Unknown platform")
    : "Unknown platform";

const dealMemoStatusLabels: Record<DealMemoStatus, string> = {
  "contract.creating": "Creating contract",
  "contract.deal_complete": "Deal complete",
  "contract.draft": "Draft contract",
  "contract.failed": "Contract creation failed",
  "contract.signed": "Contract signed",
  "contract.suggested_edits": "Contract suggested edits",
  "contract.sent": "Waiting for signers",
  "contract.viewed": "Contract viewed",
  "contract.pending": "Contract pending",
  "selected.review_deal": "Review deal",
  "shortlist.suggested": "Shortlisted",
  "shortlist.rejected": "Rejected for mission",
  "outreach.pending": "Outreach pending",
  "outreach.reachout_to_creator": "Reached out to creator",
  "outreach.message_sent": "Message sent",
  "outreach.no_response": "Didn’t respond",
  "responded.priced": "Responded with price",
  "responded.mission_edits": "Responded with mission edits",
  "responded.declined_by_creator": "Declined by creator",
  "responded.rejected_by_brand": "Rejected for mission",
};

/**
 * Formats the name of a deal memo status.
 *
 * @param status - The deal memo status to be formatted.
 * @returns The formatted name of the deal memo status.
 */
export const formatDealMemoStatus = (status: Nullable<DealMemoStatus>) =>
  status ? (dealMemoStatusLabels[status] ?? status) : status;

const brandDirectoryStatusLabels: Record<BrandDirectoryStatus, string> = {
  active: "Active",
  archived: "Archived",
};

export const formatBrandDirectoryStatus = (
  status: Nullable<BrandDirectoryStatus>,
) => (status ? (brandDirectoryStatusLabels[status] ?? status) : status);

const pitchStatusLabels: Record<PitchStatus, string> = {
  submitted: "Submitted",
  qa_approved: "QA Approved",
  qa_rejected: "QA Rejected",
};

/**
 * Returns a human-readable label for a pitch status.
 */
export const formatPitchStatus = (status: Nullable<PitchStatus>) =>
  status ? (pitchStatusLabels[status] ?? status) : status;

const currencyIcons: Record<Currencies, LucideIcon> = {
  EUR: Euro,
  GBP: PoundSterling,
  USD: DollarSign,
};

/**
 * Returns the icon component corresponding to the given currency code.
 * If the currency is not recognized or is undefined, returns the PoundSterling icon by default.
 *
 * @param currency - The currency code to get the icon for.
 * @returns The icon component for the specified currency, or PoundSterling if not found.
 */
export const formatCurrencyIcon = (currency: Nullable<Currencies>) =>
  currency ? (currencyIcons[currency] ?? PoundSterling) : PoundSterling;

/**
 * Capitalizes the first letter of each word in a comma-separated list.
 * If the input is a string, it splits the string by commas, trims each word,
 * capitalizes the first letter, and joins them with a " · " separator.
 * If the input is an array of strings, it joins the array into a single
 * comma-separated string and processes it as above.
 * If the input is undefined, it returns undefined.
 *
 * Used for KeyWords display.
 *
 * @param str - The input string or array of strings to be formatted.
 * @returns The formatted string with capitalized words separated by " · ",
 * or undefined if the input is undefined.
 */
export const capitaliseCommaList = (
  str: string[] | string | undefined,
): string | undefined => {
  if (typeof str === "string") {
    return str
      .split(",")
      .map((word) => {
        word = word.trim();
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" · ");
  } else if (Array.isArray(str)) {
    return capitaliseCommaList(str.join(","));
  } else {
    return str;
  }
};

export const formatUnixTimeStamp = (timestampStr: string): Date => {
  const timestamp = Number(timestampStr);
  return new Date(timestamp);
};

/**
 * Removes hashtags from a given string.
 * @param text - The input string containing hashtags.
 * @returns The string without hashtags.
 */
export const removeHashtags = (text: string): string =>
  text.replace(/#[^\s#]+/g, "").trim();

/**
 * Strips leading "@" symbols from a given string.
 */
export const stripAtSymbol = (value: string) => value.replace(/^@+/, "");

/**
 * Formats the video creator handle by adding an "@" prefix.
 * @param handle - The video creator handle, which may be null or undefined.
 * @returns The formatted video creator handle, or an empty string if the handle is null or undefined.
 */
export const formatVideoCreatorHandle = (handle: Nullable<string>): string =>
  handle ? `@${handle}` : "";

/**
 * Validates if the given URL is a valid TikTok URL.
 * Supports standard URLs (www.tiktok.com), wildcard subdomains (*.tiktok.com), and short URLs (vm.tiktok.com).
 * @param url - The URL to validate.
 * @returns True if the URL matches the TikTok pattern, false otherwise.
 */
export const isValidTikTokUrl = (url: string): boolean => {
  const standardPattern =
    /^https:\/\/(?:[a-zA-Z0-9-]+\.)?tiktok\.com\/[^/]+\/video\/[^/]+\/?$/;
  const shortPattern = /^https?:\/\/vm\.tiktok\.com\/[A-Za-z0-9]+\/?$/;
  return standardPattern.test(url) || shortPattern.test(url);
};

/**
 * Validates if the given URL is a valid Instagram Reel URL.
 * @param url - The URL to validate.
 * @returns True if the URL matches the Instagram Reel pattern, false otherwise.
 */
export const isValidInstagramUrl = (url: string): boolean => {
  const INSTA_REGEX =
    /^(?:https?:\/\/)?(?:www\.)?instagram\.com\/(?:p|reel|reels)\/[\w-]+(?:\/.*)?$/;
  return INSTA_REGEX.test(url);
};

/**
 * Normalizes a URL by ensuring it has a proper scheme and format.
 * Returns null if the URL is invalid or empty.
 */
export const normalizeUrl = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();

  if (trimmed.length === 0) {
    return null;
  }

  const withProtocol = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;

  try {
    return new URL(withProtocol).toString();
  } catch {
    return null;
  }
};

/**
 * Removes all line breaks from the given text.
 * @param text - The input string from which line breaks will be removed.
 * @returns The input string without line breaks.
 */
export const removeLineBreaks = (text: string) =>
  text.replace(/(\r\n|\n|\r)/gm, " ");

export const groupByProperty = <
  T extends Record<K, unknown>,
  K extends keyof T,
>(
  array: T[],
  key: K,
): T[][] => {
  const groupedObject = array.reduce(
    (acc, currentItem) => {
      const groupKey = String(currentItem[key]);
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(currentItem);
      return acc;
    },
    {} as Record<string, T[]>,
  );
  return Object.values(groupedObject);
};

/**
 * Gets the epoch time (in seconds) from a specified number of days before now
 */
export const getEpochTimeBeforeDays = (days: number): number => {
  const currentEpoch = Math.floor(Date.now() / 1000);
  const secondsInDay = 86400;
  return currentEpoch - days * secondsInDay;
};

/**
 * Gets epoch time range for a specific time period in the past
 * Returns { since, until } with epoch times in seconds
 */
export const getEpochTimeRange = (
  daysAgo: number,
  duration: number,
): { since: number; until: number } => {
  const currentEpoch = Math.floor(Date.now() / 1000);
  const secondsInDay = 86400;

  const until = currentEpoch - daysAgo * secondsInDay;
  const since = until - duration * secondsInDay;

  return { since, until };
};

/**
 * Calculates the future expiry date by adding a duration (in seconds)
 * to the current time. Useful for handling token expiration based on
 * an "expires_in" value from an API response.
 *
 * @param {string} expiresInSecondsStr - The number of seconds until expiry, as a string.
 * @returns {Date} A Date object representing the time of expiration.
 */
export const getExpiryDateFromNowInSeconds = (
  expiresInSecondsStr: string | null,
): Date => {
  const seconds = Number(expiresInSecondsStr);
  const now = Date.now();
  const expiryTime = now + seconds * 1000;
  return new Date(expiryTime);
};

/**
 * Calculates the number of days between the current date and an expiry date.
 * Returns the days as a string.
 */
export const getDaysUntilExpiry = (expiryDate: Date): string => {
  const now = new Date();
  const diffInTime = expiryDate.getTime() - now.getTime();
  const diffInDays = Math.ceil(diffInTime / (1000 * 60 * 60 * 24));

  return Math.max(0, diffInDays).toString();
};

/**
 * Calculates the future date by adding a number of days to the current date.
 */
export const getFutureDateFromDays = (days: number): Date => {
  const now = new Date();
  now.setDate(now.getDate() + days);
  return now;
};

type ServiceData = {
  icon: string;
  title: string;
  accountUrl: string;
  avgLabel: string;
  handle: string;
};

export const formatServiceData = (
  service: SocialService | null,
  username: string | null,
): ServiceData | null => {
  switch (service) {
    case "tiktok":
      return {
        icon: "/tik-tok-square.svg",
        title: "TikTok",
        accountUrl: `https://www.tiktok.com/@${username}`,
        avgLabel: "Avg. views",
        handle: `@${username}`,
      };
    case "instagram":
      return {
        icon: "/Icon-Insta.svg",
        title: "Instagram",
        accountUrl: `https://www.instagram.com/${username}`,
        avgLabel: "Avg. plays",
        handle: `@${username}`,
      };
    default:
      return null;
  }
};

const creatorOnboardingStepLabels: Record<CreatorOnboardingStep, string> = {
  welcome: "Welcome",
  whatsapp_phone_number: "Enter WhatsApp phone number",
  whatsapp_verification: "Verify for WhatsApp verification",
  enter_details: "Enter details",
  connect_socials: "Connect socials",
  enter_postal_address: "Postal address",
  representation: "Representation",
  agree_terms: "Agree to terms",
  connect_stripe: "Connect Stripe",
  complete_onboarding: "Completed",
};

/**
 * Formats the name of a creatorOnboarding step.
 *
 * @param format - The onboardingstep to be formatted.
 * @returns The formatted name of the onboardingstep format.
 */
export const formatCreatorOnboardingStep = (
  format: Nullable<CreatorOnboardingStep>,
) => (format ? (creatorOnboardingStepLabels[format] ?? format) : format);

const scrapeStepLabels: Record<SocialScrapeStep, string> = {
  scraping_profile: "Scraping profile",
  transcribing_videos: "Transcribing videos",
  generating_bio: "Generating bio",
  categorizing_creator: "Categorizing creator",
  generating_keywords: "Generating keywords",
  calculating_video_metrics: "Calculating video metrics",
  counting_swear_jar: "Counting swear jar",
  finding_controversial_topics: "Finding controversial topics",
};

/**
 * Returns a human-readable label for a creator scrape step.
 */
export const formatScrapeStep = (step: SocialScrapeStep | string): string =>
  scrapeStepLabels[step as SocialScrapeStep] ?? step;

const scrapePlatformLabels: Record<ScrapePlatform, string> = {
  instagram: "Instagram",
  tiktok: "TikTok",
};

/**
 * Returns a human-readable label for a scrape platform.
 */
export const formatScrapePlatform = (platform: ScrapePlatform): string =>
  scrapePlatformLabels[platform];

/**
 * Format file size into human-readable format
 */
export const formatFileSize = (bytes: number) => {
  if (bytes === 0) {
    return "0 B";
  }
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return (
    Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  );
};

/**
 * Removes http:// or https:// protocol from a URL string.
 */
export const removeHttpProtocol = (url: string): string =>
  url.replace(/^https?:\/\//, "");

const IMAGE_MIME_REGEX = /image\/(jpeg|jpg|png|webp|gif)/i;
const IMAGE_URL_EXTENSION_REGEX = /\.(jpg|jpeg|png|webp|gif)(\?|$)/i;

/**
 * Extracts image file extension from content type header, normalizing 'jpeg' to 'jpg'
 */
export const getImageExtensionFromContentType = (
  type: string | null,
): string | null => {
  if (!type) {
    return null;
  }
  const mimeMatch = type.match(IMAGE_MIME_REGEX);
  if (!mimeMatch) {
    return null;
  }
  const ext = mimeMatch[1].toLowerCase();
  return ext === "jpeg" ? "jpg" : ext;
};

/**
 * Extracts image file extension from URL, normalizing 'jpeg' to 'jpg'
 */
export const getImageExtensionFromUrl = (url: string): string | null => {
  const urlExtension = url.match(IMAGE_URL_EXTENSION_REGEX);
  if (!urlExtension) {
    return null;
  }
  const ext = urlExtension[1].toLowerCase();
  return ext === "jpeg" ? "jpg" : ext;
};

/**
 * Extracts the Firebase Storage path from a URL pathname.
 * Format: /v0/b/{bucket}/o/{path}
 * Returns the decoded path or null if not found.
 */
export const extractFirebaseStoragePathFromUrlPath = (
  pathname: string,
): string | null => {
  const pathMatch = pathname.match(/\/o\/(.+)$/);
  if (!pathMatch) {
    return null;
  }
  return decodeURIComponent(pathMatch[1]);
};

/**
 * Builds a link-in-bio URL using the provided public slug.
 */
export const buildLinkInBioUrl = (publicSlug: string | null): string =>
  `${process.env.NEXT_PUBLIC_LINKINBIO_URL}/${publicSlug}`;

/**
 * Builds a public profile URL for the supported link-in-bio services using the provided identifier.
 * Returns null when the service requires a custom link input.
 */
export const buildLinkInBioChannelUrl = (
  service: LinkInBioSocialService,
  identifier: string,
): string | null => {
  const sanitizedIdentifier = identifier.trim();

  if (!sanitizedIdentifier) {
    return null;
  }

  switch (service) {
    case "tiktok": {
      const handle = stripAtSymbol(sanitizedIdentifier);
      return `https://www.tiktok.com/@${encodeURIComponent(handle)}`;
    }
    case "instagram": {
      const handle = stripAtSymbol(sanitizedIdentifier);
      return `https://www.instagram.com/${encodeURIComponent(handle)}`;
    }
    case "youtube": {
      return `https://www.youtube.com/${encodeURIComponent(sanitizedIdentifier)}`;
    }
    case "twitch": {
      return `https://www.twitch.tv/${encodeURIComponent(sanitizedIdentifier)}`;
    }
    case "cameo": {
      const handle = stripAtSymbol(sanitizedIdentifier);
      return `https://www.cameo.com/${encodeURIComponent(handle)}`;
    }
    default:
      return null;
  }
};

/**
 * Normalizes the identifier value for storage by trimming whitespace and removing leading @ for handle-based services.
 */
export const normalizeChannelIdentifier = (
  service: LinkInBioSocialService,
  identifier: string,
): string => {
  const sanitizedIdentifier = identifier.trim();

  switch (service) {
    case "tiktok":
    case "instagram":
    case "cameo":
      return stripAtSymbol(sanitizedIdentifier.toLowerCase());
    default:
      return sanitizedIdentifier;
  }
};

/**
 * Generates a URL-safe slug from the creator name.
 */
export const formatPublicSlugFromName = (name: string): string => {
  const slug = name
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return slug || "creator";
};

/**
 * "+" in an url is interpreted as a space, so we need to replace it with the actual plus sign
 */
export const formatPhoneNumberFromUrl = (urlPhoneNumber: string): string =>
  urlPhoneNumber.replace(/ /g, "+").trim();

/**
 * Extracts username from social media URLs or handles.
 * Handles various formats including full URLs, short URLs, and plain usernames.
 * Removes @ symbols and extracts the username from URL paths.
 */
export const extractUsernameFromUrl = (input: string): string => {
  if (!input) {
    return "";
  }

  const trimmed = input.trim();

  if (trimmed.startsWith("@") && !trimmed.includes("/")) {
    return stripAtSymbol(trimmed);
  }

  try {
    const urlString = /^https?:\/\//i.test(trimmed)
      ? trimmed
      : `https://${trimmed}`;

    const url = new URL(urlString);
    const pathname = url.pathname;

    const cleanPath = pathname.replace(/^\/+|\/+$/g, "");

    if (!cleanPath) {
      return trimmed;
    }

    const segments = cleanPath.split("/");
    const firstSegment = segments[0];

    if (url.hostname.includes("youtube.com")) {
      if (
        firstSegment &&
        (firstSegment === "c" ||
          firstSegment === "user" ||
          firstSegment === "channel")
      ) {
        return segments[1] ? `/${firstSegment}/${segments[1]}` : trimmed;
      }
      return firstSegment;
    }

    return stripAtSymbol(firstSegment);
  } catch {
    const manualMatch = trimmed.match(
      /(?:https?:\/\/)?(?:[^/]+)\/(.+?)(?:\/|$)/,
    );
    if (manualMatch?.[1]) {
      return stripAtSymbol(manualMatch[1]);
    }

    return trimmed;
  }
};

export const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export const toPercentString = (value: number) => `${value}%`;

/**
 * Calculates width and height dimensions for 9:16 aspect ratio.
 */
export const calculateDimensionsFor9By16 = (height: number) => {
  const width = Math.round(height * (9 / 16));
  return { width, height };
};

/**
 * Strips leading @, trims whitespace, and lowercases a social media handle.
 */
export const normalizeSocialHandle = (handle: string): string =>
  handle.trim().replace(/^@/, "").toLowerCase();

/**
 * Returns a handle prefixed with @ for display (e.g. "creator" → "@creator").
 */
export const formatSocialHandle = (handle: string): string =>
  `@${handle.replace(/^@/, "")}`;
